import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FractalCanvas } from './FractalCanvas';
import { EXPLORER_PALETTE } from './fractalPalette';
import { cappedBackingSize, watchDevicePixelRatio } from './ResponsiveFractalCanvas';

const RESIZE_SETTLE_MS = 160;

function renderSize(section) {
  return cappedBackingSize(section.clientWidth, section.clientHeight);
}

function initialView({ width, height }, aspect) {
  const spanY = Math.max(2.74, 3.5 / aspect);
  const spanX = spanY * aspect;
  return {
    width,
    height,
    xRange: [-0.75 - spanX / 2, -0.75 + spanX / 2],
    yRange: [-spanY / 2, spanY / 2],
  };
}

export function ViewportFractalCanvas({ preferredRenderer }) {
  const sectionRef = useRef(null);
  const previewRef = useRef(null);
  const previewVisibleRef = useRef(false);
  const previewTargetRef = useRef(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [resizing, setResizing] = useState(false);
  const [viewport, setViewport] = useState(null);

  useLayoutEffect(() => {
    const section = sectionRef.current;
    let committed = null;
    let committedCssWidth = null;
    let committedCssHeight = null;
    let resizeTimer = null;
    const retainCpuFrame = size => {
      if (previewVisibleRef.current) {
        previewTargetRef.current = size;
        return;
      }
      const source = section.querySelector('.image-container > canvas:not([aria-hidden])');
      const deep = section.querySelector('.image-container > canvas[aria-hidden]');
      if (!source || source._useWebGL !== false || deep?.style.visibility === 'visible') return;
      const preview = previewRef.current;
      const context = preview?.getContext('2d');
      if (!context) return;
      try {
        preview.width = source.width;
        preview.height = source.height;
        context.drawImage(source, 0, 0);
      } catch {
        return;
      }
      previewVisibleRef.current = true;
      previewTargetRef.current = size;
      setPreviewVisible(true);
    };
    const commit = size => {
      if (committed) retainCpuFrame(size);
      committed = size;
      committedCssWidth = section.clientWidth;
      committedCssHeight = section.clientHeight;
      setResizing(false);
      setViewport(previous => previous ? { ...previous, ...size }
        : initialView(size, committedCssWidth / committedCssHeight));
    };
    const updateSize = (immediate = false) => {
      const size = renderSize(section);
      if (!size) return;
      if (!committed) {
        commit(size);
        return;
      }
      clearTimeout(resizeTimer);
      resizeTimer = null;
      if (committed.width === size.width && committed.height === size.height) {
        setResizing(false);
        return;
      }
      if (immediate === true && committedCssWidth === section.clientWidth
        && committedCssHeight === section.clientHeight) {
        commit(size);
        return;
      }
      setResizing(true);
      // CSS keeps the previous image at its vertical scale while the window moves.
      // Replacing the backing buffer at every intermediate size clears it and
      // restarts an expensive render, so refine only the final viewport.
      resizeTimer = setTimeout(() => {
        resizeTimer = null;
        commit(size);
      }, RESIZE_SETTLE_MS);
    };

    updateSize();
    const stopWatchingDpr = watchDevicePixelRatio(() => updateSize(true));
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateSize);
    observer?.observe(section);
    window.addEventListener('resize', updateSize);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateSize);
      stopWatchingDpr();
      clearTimeout(resizeTimer);
      previewVisibleRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!previewVisible) return;
    const deep = sectionRef.current?.querySelector('.image-container > canvas[aria-hidden]');
    if (!deep) return;
    const clearIfDeepVisible = () => {
      if (deep.style.visibility !== 'visible') return;
      previewVisibleRef.current = false;
      previewTargetRef.current = null;
      setPreviewVisible(false);
    };
    clearIfDeepVisible();
    const observer = new MutationObserver(clearIfDeepVisible);
    observer.observe(deep, { attributes: true, attributeFilter: ['style'] });
    return () => observer.disconnect();
  }, [previewVisible]);

  return <section className={`section${resizing || previewVisible ? ' section-resizing' : ''}${previewVisible ? ' section-cpu-preview' : ''}`}
    data-section="viewport-canvas" ref={sectionRef}>
    {viewport && <FractalCanvas
      width={viewport.width}
      height={viewport.height}
      xRange={viewport.xRange}
      yRange={viewport.yRange}
      palette={EXPLORER_PALETTE}
      preferredRenderer={preferredRenderer}
      showFullscreenButton={false}
      onCpuFrameReady={({ width, height }) => {
        const target = previewTargetRef.current;
        if (!previewVisibleRef.current || width !== target?.width || height !== target?.height) return;
        previewVisibleRef.current = false;
        previewTargetRef.current = null;
        setPreviewVisible(false);
      }}
    />}
    <canvas ref={previewRef} className="viewport-resize-preview" aria-hidden="true"
      style={{ visibility: previewVisible ? 'visible' : 'hidden' }} />
  </section>;
}
